/**
 * Search & Remediate v2 on the web: the canonical filter (ErSnrQuery v1) and every shape the
 * v2 endpoints exchange. A mirror of `apps/api/src/modules/er/snr-query/types.ts` (the AST and
 * the compile result: the API file wins) and of `contracts/snr-api.md` section 4 (the run,
 * message, mailbox, facet, action and saved-search shapes).
 *
 * The browser never writes a provider query and never compiles: it builds this tree, the
 * server validates and compiles it, and the page only shows what comes back. Remediation
 * moves mail, it never deletes it (owner, 27 Oct 2026): the four verbs are Quarantine, Junk,
 * Move and Inbox. `Trash` and `Delete` stay in `lib/er/types.ts` (ErSnrAction) so old runs
 * still read, but nothing here offers them.
 */

export const SNR_QUERY_VERSION = 1 as const

export const SNR_FIELDS = [
  'sender.address',
  'sender.domain',
  'sender.name',
  'recipient.to',
  'recipient.cc',
  'recipient.any',
  'subject',
  'body',
  'link.domain',
  'attachment.present',
  'attachment.name',
  'attachment.type',
  'attachment.sha256',
  'size',
  'received',
  'internetMessageId',
  'folder',
  'isRead',
  'isFlagged',
  'importance',
  'header',
] as const
export type SnrField = (typeof SNR_FIELDS)[number]

export const SNR_OPS = [
  'is',
  'isAnyOf',
  'isOrUnder',
  'domainIs',
  'hasWords',
  'contains',
  'startsWith',
  'largerThan',
  'smallerThan',
  'after',
  'before',
  'between',
  'withinLast',
  'exists',
] as const
export type SnrOp = (typeof SNR_OPS)[number]

export const SNR_FOLDERS = ['inbox', 'junk', 'deleted', 'archive', 'other'] as const
export type SnrFolder = (typeof SNR_FOLDERS)[number]

export const SNR_IMPORTANCE = ['high', 'normal', 'low'] as const
export type SnrImportance = (typeof SNR_IMPORTANCE)[number]

export const SNR_RELATIVE_UNITS = ['hours', 'days', 'weeks'] as const
export type SnrRelativeUnit = (typeof SNR_RELATIVE_UNITS)[number]

/** A calendar day in the WORKSPACE zone ('yyyy-MM-dd'), or an exact instant (ISO, Z). */
export type SnrDatePoint = { date: string } | { at: string }

export interface SnrCondition {
  kind: 'condition'
  field: SnrField
  op: SnrOp
  negate?: boolean
  /** Shape depends on field + op (catalogue.ts, the spec's "Fields, operators and values"). */
  value?: unknown
  /** The header name, for field 'header' only. */
  header?: string
}

export interface SnrGroup {
  kind: 'group'
  match: 'all' | 'any'
  negate?: boolean
  /** 1 to 20 children. */
  children: SnrNode[]
}

export type SnrNode = SnrGroup | SnrCondition

export interface ErSnrQuery {
  version: typeof SNR_QUERY_VERSION
  root: SnrGroup
  options?: {
    includeSentAndDrafts?: boolean
    includeInlineAttachments?: boolean
  }
}

/** Refused beyond these, each with an errorCode (the server is the authority). */
export const SNR_LIMITS = {
  maxDepth: 3,
  maxConditions: 40,
  maxChildren: 20,
  maxAnyOf: 50,
  maxShaValues: 20,
  maxSubjectLength: 500,
  maxBodyLength: 500,
  maxNameLength: 200,
} as const

// ── Scope ───────────────────────────────────────────────────────────────────────

export interface SnrScope {
  /** [] = every domain the caller may access; outside access = 403, never a silent drop. */
  domainIds: string[]
  mailboxes:
    | { mode: 'all' }
    | { mode: 'groups'; groupIds: string[] }
    | { mode: 'list'; recipientIds: string[] }
  excludeRecipientIds?: string[]
}

// ── Compile (POST /compile) ─────────────────────────────────────────────────────

export type SnrProvider = 'microsoft' | 'google'

/** exact: the provider's answer equals the filter's; narrows: a superset; none: evaluator only. */
export type SnrPushdown = 'exact' | 'narrows' | 'none'

/** A warning, a blocker or a validation problem. `code` is the errorCode the web maps to a key. */
export interface SnrIssue {
  code: string
  /** Child indexes from the root, dot-separated ('0.2.1'); absent for the whole query. */
  path?: string
  params?: Record<string, string | number>
}

export interface SnrConditionBadge {
  path: string
  microsoft: SnrPushdown
  google: SnrPushdown
}

export interface SnrGraphPlan {
  mode: 'filter' | 'search'
  expression: string
  folders: string[]
  widened: string[]
  score: { filter: number; search: number }
  /**
   * Asked once instead when Microsoft refuses `expression` (a 400: too complex): the same query without
   * the least selective AND-ed clause, which the server still checks after the search. Its `widened` is
   * this plan's plus `fallback_without@<path>`. Null: nothing safe to drop.
   */
  fallback: { expression: string; widened: string[] } | null
}

export interface SnrGmailPlan {
  queries: string[]
  excludesSentAndDrafts: boolean
  widened: string[]
}

export type SnrEnrichment = 'headers' | 'body' | 'attachments' | 'attachmentBytes'

export interface SnrCompileResult {
  normalized: ErSnrQuery
  providers: SnrProvider[]
  plans: { microsoft: SnrGraphPlan | null; google: SnrGmailPlan | null }
  badges: SnrConditionBadge[]
  warnings: SnrIssue[]
  /** Non-empty = the run cannot start (e.g. er_snr_query_unbounded). */
  blockers: SnrIssue[]
  planHash: string
  /** The workspace zone the dates were resolved in. */
  timeZone: string
  resolved: { receivedFrom?: string; receivedTo?: string }
  enrichment: { microsoft: SnrEnrichment[]; google: SnrEnrichment[] }
  /** Paths of the "has words" conditions a running plan sends to a provider (decided with strict words after the search). */
  strictWordPaths: string[]
}

// ── Runs ────────────────────────────────────────────────────────────────────────

export type SnrRunStatus =
  | 'Pending'
  | 'Searching'
  | 'Searched'
  | 'SearchedWithGaps'
  | 'Paused'
  | 'Cancelled'
  | 'Failed'

export interface SnrFailedByReason {
  reasonCode: string
  retryable: boolean
  count: number
}

export interface SnrRunProgress {
  id: string
  mode: 'search' | 'dry_run'
  status: SnrRunStatus
  mailboxes: { total: number; done: number; failed: number; skipped: number; remaining: number }
  matches: number
  failedByReason: SnrFailedByReason[]
  /** Dry runs only. */
  estimate?: { sampledMailboxes: number; matchedInSample: number; low: number; high: number }
  startedAt: string | null
  finishedAt: string | null
  timeZone: string
}

/**
 * The verbs (owner, 27 Oct 2026, DECISIONS #1): four that move mail, Trash (Deleted Items / Gmail Trash, can be
 * undone) and Delete (permanent, cannot be undone; behind delete:ErHardDelete, the workspace's hard-delete switch and
 * a provider that grants it: Microsoft does, Google does not today).
 */
export const SNR_ACTION_VERBS = ['Quarantine', 'Junk', 'Move', 'Inbox', 'Trash', 'Delete'] as const
export type SnrActionVerb = (typeof SNR_ACTION_VERBS)[number]

/** What a job that paused itself is missing (the API's `pause`, contracts/snr-api.md "Changes", restore-delete-snr). */
export type SnrMissingPermission = 'role' | 'switch' | 'provider_scope' | 'mail_permission'

export interface SnrJobPause {
  reason: 'permission_missing'
  missing: SnrMissingPermission
  provider: SnrProvider | null
  domainId: string | null
  mailbox: string | null
}

/** GET /settings/delete-gate: what a permanent Delete needs here, so the menu says which one is missing. */
export interface SnrDeleteGate {
  canHardDelete: boolean
  hardDeleteEnabled: boolean
  cap: number
  providers: { microsoft: boolean; google: boolean }
}

/** What a copy's last action left it as, plus the legacy values an old run may carry. */
export type SnrMessageActionStatus = 'Pending' | 'Running' | 'Succeeded' | 'Failed' | 'Skipped'

export interface SnrMessageRow {
  id: string
  recipientId: string
  mailbox: string
  domainId: string
  provider: SnrProvider
  internetMessageId: string | null
  subject: string
  senderAddress: string
  senderName: string | null
  /** ISO, Z. */
  receivedAt: string
  folder: SnrFolder
  sizeBytes: number | null
  hasAttachments: boolean
  attachmentNames: string[]
  linkHosts: string[]
  /** `action` may be a legacy verb (Trash, Delete, Label) on a run made before v2: read-only. */
  lastAction: { action: string; status: SnrMessageActionStatus; at: string } | null
}

export type SnrMailboxStatus = 'Pending' | 'Searching' | 'Searched' | 'Failed' | 'Skipped' | 'Capped'

export interface SnrMailboxRow {
  recipientId: string
  email: string
  domainId: string
  provider: SnrProvider
  status: SnrMailboxStatus
  reasonCode: string | null
  retryable: boolean | null
  matches: number
  pagesRead: number
  startedAt: string | null
  finishedAt: string | null
}

export interface SnrFacets {
  senders: Array<{ value: string; count: number }>
  domains: Array<{ domainId: string; count: number }>
  folders: Array<{ value: string; count: number }>
  providers: Array<{ value: SnrProvider; count: number }>
}

// ── Remediation (preview, confirm, job) ─────────────────────────────────────────

/**
 * The filters of the results table that the stored-column `where` cannot express (contract
 * "Changes", 27 Oct, snr-web): the toolbar search, the Domain and Provider facets and a mailbox's
 * drill-down. The list, the facets, the preview and the selection all carry the same ones.
 */
export interface SnrResultFilters {
  search?: string
  domainIds?: string[]
  providers?: SnrProvider[]
  recipientIds?: string[]
}

/** A predicate, never a page of ids: the server freezes it into one action row per copy. */
export type SnrSelection =
  | { mode: 'all_matching'; where?: ErSnrQuery; filters?: SnrResultFilters; excludeIds?: string[] }
  | { mode: 'ids'; ids: string[] }

export interface SnrActionPreview {
  messages: number
  mailboxes: number
  domains: number
  byProvider: { microsoft: number; google: number }
  distinctInternetMessageIds: number
  /** False for a Delete only: it cannot be undone. */
  reversible: boolean
  /** True above 25 messages, and for every Delete. */
  requiresTypedCount: boolean
  /** The most this action may act on (a Delete: at most 1,000). */
  cap: number
  confirmToken: string
  expiresAt: string
  warnings: SnrIssue[]
}

export type SnrActionJobStatus = 'Pending' | 'Running' | 'Paused' | 'Done' | 'DoneWithFailures' | 'Cancelled'

export interface SnrActionProgress {
  jobId: string
  action: SnrActionVerb
  status: SnrActionJobStatus
  total: number
  done: number
  failed: number
  skipped: number
  remaining: number
  failedByReason: SnrFailedByReason[]
  parentJobId: string | null
  kind: 'action' | 'retry' | 'undo'
  /** Set when the job paused ITSELF for a missing permission (what, where); null otherwise. Optional: an older API. */
  pause?: SnrJobPause | null
}

/** A row of GET /:id/jobs: one remediation job of a run (an action, or a retry or an undo of one), newest first. */
export interface SnrJobRow {
  jobId: string
  action: SnrActionVerb
  kind: SnrActionProgress['kind']
  parentJobId: string | null
  status: SnrActionJobStatus
  /** Move only. */
  folderName: string | null
  comment: string | null
  createdById: string | null
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  total: number
  done: number
  failed: number
  skipped: number
  remaining: number
  /** Set when the job paused ITSELF for a missing permission; null otherwise. Optional: an older API. */
  pause?: SnrJobPause | null
}

/** POST /actions/:jobId/undo/preview: how many copies an undo would put back, and the token to confirm it with. */
export interface SnrUndoPreview {
  messages: number
  /** True above 25 copies: the analyst types the count and the confirm carries the token. */
  requiresTypedCount: boolean
  confirmToken: string | null
  expiresAt: string | null
  /** False for a Delete: nothing can come back. Optional: an older API answers without it. */
  reversible?: boolean
  /** How many copies the job deleted for good (a Delete); 0 for every other verb. */
  permanentlyDeleted?: number
}

/** GET /:id/messages/:messageId/headers. */
export interface SnrMessageHeaders {
  messageId: string
  headers: Array<{ name: string; value: string }>
  /** 'stored': what the search kept. 'provider': read from the mailbox now. */
  source: 'stored' | 'provider'
  fetchedAt: string
}

export interface SnrSavedSearch {
  id: string
  name: string
  query: ErSnrQuery
  shared: boolean
  ownerId: string
  createdAt: string
  updatedAt: string
}

/**
 * What a v2 run adds to a row of the Logs list (`GET /`). A run made before v2 carries none of
 * it (`queryVersion` null) and keeps rendering through its legacy criteria.
 */
export interface SnrRunListExtras {
  queryVersion?: number | null
  query?: ErSnrQuery | null
  mode?: 'search' | 'dry_run' | null
  name?: string | null
  startedAt?: string | null
  finishedAt?: string | null
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

/** The run list spells the status as the enum label ('Searched With Gaps'), /progress as one word. */
export function normalizeSnrRunStatus(status: string | undefined | null): SnrRunStatus | string | undefined {
  if (status === 'Searched With Gaps') return 'SearchedWithGaps'
  return status ?? undefined
}

/** True while a run is still doing work: the progress poll continues. */
export function isSnrRunActive(status: string | undefined): boolean {
  const s = normalizeSnrRunStatus(status)
  return s === 'Pending' || s === 'Searching' || s === 'Paused'
}

/** True once the search phase is over and the results can be acted on. */
export function isSnrRunSearched(status: string | undefined): boolean {
  const s = normalizeSnrRunStatus(status)
  return s === 'Searched' || s === 'SearchedWithGaps'
}

/** A row of the Logs list that the v2 pipeline wrote. */
export function isSnrV2Run(row: SnrRunListExtras | null | undefined): boolean {
  return !!row && typeof row.queryVersion === 'number'
}

/** True while an action job is moving copies: the working spinner shows and Pause / Cancel are offered. */
export function isSnrActionActive(status: SnrActionJobStatus | undefined): boolean {
  return status === 'Pending' || status === 'Running'
}

/**
 * True while an action job is not over: working, or paused. The progress poll continues for a paused job too, so
 * a Resume from the activity dock (or another tab) shows here within two seconds.
 */
export function isSnrActionOpen(status: SnrActionJobStatus | undefined): boolean {
  return isSnrActionActive(status) || status === 'Paused'
}
